import { trackCalculatorComplete } from "@/components/analytics/conversions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { seo } from "@/utils/seo";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Award,
  HelpCircle,
  Lightbulb,
  Microscope,
  Rocket,
  Target,
  Trophy,
} from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_app/essay/wilks-calculator")({
  component: WilksCalculator,
  head: () => ({
    meta: seo({
      title:
        "Wilks Skoru Hesaplayıcı - Powerlifting Performansı | AtalayTheCoach",
      description:
        "Powerlifting performansınızı vücut ağırlığınıza göre normalize edin. Wilks skoru ile güç seviyenizi dünya klasmanıyla karşılaştırın.",
      keywords:
        "wilks hesaplama, wilks score, powerlifting puanlama, güç sporları, Alican Atalay",
      canonical: "https://atalaythecoach.com/essay/wilks-calculator",
    }),
  }),
});

// Wilks Katsayıları
const wilksCoefficients = {
  male: {
    a: -216.0475144,
    b: 16.2606339,
    c: -0.002388645,
    d: -0.00113732,
    e: 7.01863e-6,
    f: -1.291e-8,
  },
  female: {
    a: 594.31747775582,
    b: -27.23842536447,
    c: 0.82112226871,
    d: -0.00930733913,
    e: 4.731582e-5,
    f: -9.054e-8,
  },
};

// Wilks Skoru Hesaplama
function calculateWilksCoefficient(bodyweight: number, gender: string): number {
  const coeff = wilksCoefficients[gender as keyof typeof wilksCoefficients];
  const x = bodyweight;

  return (
    500 /
    (coeff.a +
      coeff.b * x +
      coeff.c * x * x +
      coeff.d * x * x * x +
      coeff.e * x * x * x * x +
      coeff.f * x * x * x * x * x)
  );
}

// Performans Seviyeleri
const getPerformanceLevel = (wilksScore: number, gender: string) => {
  const levels = {
    male: [
      {
        min: 0,
        max: 300,
        level: "Başlangıç",
        color: "bg-muted text-muted-foreground",
      },
      {
        min: 300,
        max: 400,
        level: "Orta",
        color: "bg-secondary text-secondary-foreground",
      },
      {
        min: 400,
        max: 500,
        level: "İyi",
        color: "bg-primary text-primary-foreground",
      },
      {
        min: 500,
        max: 600,
        level: "Çok İyi",
        color: "bg-accent text-accent-foreground",
      },
      {
        min: 600,
        max: 700,
        level: "Elit",
        color: "bg-accent text-accent-foreground",
      },
      {
        min: 700,
        max: Number.POSITIVE_INFINITY,
        level: "Dünya Klasmanı",
        color: "bg-destructive text-destructive-foreground",
      },
    ],
    female: [
      {
        min: 0,
        max: 250,
        level: "Başlangıç",
        color: "bg-muted text-muted-foreground",
      },
      {
        min: 250,
        max: 350,
        level: "Orta",
        color: "bg-secondary text-secondary-foreground",
      },
      {
        min: 350,
        max: 450,
        level: "İyi",
        color: "bg-primary text-primary-foreground",
      },
      {
        min: 450,
        max: 550,
        level: "Çok İyi",
        color: "bg-accent text-accent-foreground",
      },
      {
        min: 550,
        max: 650,
        level: "Elit",
        color: "bg-accent text-accent-foreground",
      },
      {
        min: 650,
        max: Number.POSITIVE_INFINITY,
        level: "Dünya Klasmanı",
        color: "bg-destructive text-destructive-foreground",
      },
    ],
  };

  const genderLevels = levels[gender as keyof typeof levels];
  const level = genderLevels.find(
    (l) => wilksScore >= l.min && wilksScore < l.max,
  );
  return level || genderLevels[0];
};

function WilksCalculator() {
  const [gender, setGender] = useState<string>("");
  const [bodyweight, setBodyweight] = useState<string>("");
  const [squat, setSquat] = useState<string>("");
  const [bench, setBench] = useState<string>("");
  const [deadlift, setDeadlift] = useState<string>("");
  const [results, setResults] = useState<{
    total: number;
    wilksCoeff: number;
    wilksScore: number;
    performanceLevel: any;
  } | null>(null);

  const calculateWilks = () => {
    const bw = Number.parseFloat(bodyweight);
    const sq = Number.parseFloat(squat) || 0;
    const bp = Number.parseFloat(bench) || 0;
    const dl = Number.parseFloat(deadlift) || 0;

    if (!bw || !gender || sq + bp + dl === 0) return;

    const total = sq + bp + dl;
    const wilksCoeff = calculateWilksCoefficient(bw, gender);
    const wilksScore = total * wilksCoeff;
    const performanceLevel = getPerformanceLevel(wilksScore, gender);

    setResults({
      total,
      wilksCoeff,
      wilksScore,
      performanceLevel,
    });
    trackCalculatorComplete("wilks");
  };

  return (
    <div className="min-h-screen bg-background pt-32 pb-16">
      <div className="container mx-auto max-w-4xl px-4">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full border border-border bg-card p-4">
              <Trophy className="h-12 w-12 text-primary" />
            </div>
          </div>
          <h1 className="mb-6 font-bold text-4xl text-foreground leading-tight md:text-5xl lg:text-6xl">
            Wilks Skoru Hesaplayıcı
          </h1>
          <p className="mx-auto max-w-3xl text-lg text-muted-foreground leading-relaxed md:text-xl">
            Powerlifting performansınızı vücut ağırlığınıza göre normalize edin.
            Wilks skoru ile farklı ağırlık kategorilerindeki sporcuları
            karşılaştırın.
          </p>
        </div>

        <p className="mb-6 text-center">
          <Link
            to="/essay/ipf-gl-calculator"
            className="text-primary underline underline-offset-4"
          >
            IPF GL Points hesaplayıcısına geç
          </Link>
        </p>
        <div className="grid gap-8 md:grid-cols-2">
          {/* Hesaplayıcı Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-foreground" />
                Wilks Skoru Hesapla
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Cinsiyet</Label>
                <RadioGroup value={gender} onValueChange={setGender}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="male" id="male-wilks" />
                    <Label htmlFor="male-wilks">Erkek</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="female" id="female-wilks" />
                    <Label htmlFor="female-wilks">Kadın</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bodyweight">Vücut Ağırlığı (kg)</Label>
                <Input
                  id="bodyweight"
                  type="number"
                  placeholder="75"
                  value={bodyweight}
                  onChange={(e) => setBodyweight(e.target.value)}
                />
              </div>

              <div className="space-y-4">
                <h4 className="font-medium">Kaldırış Ağırlıkları (kg)</h4>

                <div className="space-y-2">
                  <Label htmlFor="squat">Squat</Label>
                  <Input
                    id="squat"
                    type="number"
                    placeholder="150"
                    value={squat}
                    onChange={(e) => setSquat(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bench">Bench Press</Label>
                  <Input
                    id="bench"
                    type="number"
                    placeholder="100"
                    value={bench}
                    onChange={(e) => setBench(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="deadlift">Deadlift</Label>
                  <Input
                    id="deadlift"
                    type="number"
                    placeholder="180"
                    value={deadlift}
                    onChange={(e) => setDeadlift(e.target.value)}
                  />
                </div>
              </div>

              <Button
                onClick={calculateWilks}
                className="w-full"
                disabled={
                  !bodyweight || !gender || (!squat && !bench && !deadlift)
                }
              >
                Wilks Skoru Hesapla
              </Button>
            </CardContent>
          </Card>

          {/* Sonuçlar */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-foreground" />
                Sonuçlarınız
              </CardTitle>
            </CardHeader>
            <CardContent>
              {results ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg border border-border bg-card p-4">
                      <div className="font-bold text-2xl text-foreground">
                        {results.total} kg
                      </div>
                      <div className="text-muted-foreground text-sm">
                        Toplam (SBD)
                      </div>
                    </div>
                    <div className="rounded-lg border border-border bg-card p-4">
                      <div className="font-bold text-2xl text-foreground">
                        {results.wilksCoeff.toFixed(4)}
                      </div>
                      <div className="text-muted-foreground text-sm">
                        Wilks Katsayısı
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-border bg-card p-4">
                    <div className="text-center">
                      <div className="font-bold text-3xl text-foreground">
                        {results.wilksScore.toFixed(1)}
                      </div>
                      <div className="text-muted-foreground text-sm">
                        Wilks Skoru
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold">Performans Seviyesi:</h4>
                    <div
                      className={`rounded-lg p-3 text-center ${results.performanceLevel.color}`}
                    >
                      <div className="font-bold text-lg">
                        {results.performanceLevel.level}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <h4 className="font-semibold">Kaldırış Detayları:</h4>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="rounded border border-border bg-card p-2 text-center">
                        <div className="font-bold">{squat || 0} kg</div>
                        <div className="text-xs">Squat</div>
                      </div>
                      <div className="rounded border border-border bg-card p-2 text-center">
                        <div className="font-bold">{bench || 0} kg</div>
                        <div className="text-xs">Bench</div>
                      </div>
                      <div className="rounded border border-border bg-card p-2 text-center">
                        <div className="font-bold">{deadlift || 0} kg</div>
                        <div className="text-xs">Deadlift</div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded border border-border bg-card p-3">
                    <p className="text-muted-foreground text-sm">
                      <strong>Not:</strong> Wilks skoru, farklı vücut
                      ağırlıklarındaki sporcuları adil bir şekilde
                      karşılaştırmak için kullanılır.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  Hesaplama yapmak için sol taraftaki formu doldurun
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Modern Bilgilendirici İçerik */}
        <div className="mt-16 space-y-12">
          {/* Hero Section */}
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12">
            <div className="grid items-center gap-8 md:grid-cols-2">
              <div>
                <h2 className="mb-4 font-bold text-3xl text-foreground">
                  Wilks Skoru ile Güçünüzü Keşfedin
                </h2>
                <p className="mb-6 text-lg text-muted-foreground">
                  Vücut ağırlığınız 60kg mı 100kg mi? Wilks skoru sayesinde
                  artık bu önemli değil. Gerçek gücünüzü objektif bir şekilde
                  ölçün.
                </p>
              </div>
              <div className="text-center">
                <Trophy className="mx-auto h-16 w-16 text-primary" />
              </div>
            </div>
          </div>

          {/* Interactive Cards */}
          <div className="grid gap-6 md:grid-cols-3">
            <div className="group rounded-xl border border-border bg-card p-6 transition-all duration-300 hover:shadow-lg">
              <div className="mb-4 text-3xl transition-transform group-hover:scale-110">
                <Target className="h-8 w-8 text-primary" />
              </div>
              <h3 className="mb-3 font-semibold text-foreground text-xl">
                Anında Hesaplama
              </h3>
              <p className="text-muted-foreground">
                Sadece ağırlığınızı ve kaldırışlarınızı girin. Wilks skorunuz
                anında hesaplanır.
              </p>
            </div>

            <div className="group rounded-xl border border-border bg-card p-6 transition-all duration-300 hover:shadow-lg">
              <div className="mb-4 text-3xl transition-transform group-hover:scale-110">
                <Lightbulb className="h-8 w-8 text-primary" />
              </div>
              <h3 className="mb-3 font-semibold text-foreground text-xl">
                Performans Analizi
              </h3>
              <p className="text-muted-foreground">
                Hangi seviyede olduğunuzu öğrenin: Başlangıç, Orta, İyi, Elit
                veya Dünya Klasmanı.
              </p>
            </div>

            <div className="group rounded-xl border border-border bg-card p-6 transition-all duration-300 hover:shadow-lg">
              <div className="mb-4 text-3xl transition-transform group-hover:scale-110">
                <Target className="h-8 w-8 text-primary" />
              </div>
              <h3 className="mb-3 font-semibold text-foreground text-xl">
                Hedef Belirleme
              </h3>
              <p className="text-muted-foreground">
                Bir sonraki seviyeye geçmek için ne kadar daha güçlenmeniz
                gerektiğini görün.
              </p>
            </div>
          </div>

          {/* FAQ Accordion Style */}
          <div className="rounded-2xl border border-border bg-card p-8">
            <h3 className="mb-8 text-center font-bold text-2xl text-foreground">
              <Lightbulb className="mr-2 inline h-6 w-6 text-primary" />
              Sık Sorulan Sorular
            </h3>

            <div className="space-y-6">
              <div className="group">
                <div className="rounded-lg border-border border-l-4 bg-card p-4">
                  <h4 className="mb-2 flex items-center gap-2 font-semibold text-foreground">
                    <HelpCircle className="h-5 w-5 text-primary" />
                    Wilks skoru ne kadar yüksek olmalı?
                  </h4>
                  <p className="pl-6 text-muted-foreground">
                    <strong>Erkekler:</strong> 400+ İyi, 500+ Çok İyi, 600+ Elit
                    seviye
                    <br />
                    <strong>Kadınlar:</strong> 350+ İyi, 450+ Çok İyi, 550+ Elit
                    seviye
                  </p>
                </div>
              </div>

              <div className="group">
                <div className="rounded-lg border-border border-l-4 bg-card p-4">
                  <h4 className="mb-2 flex items-center gap-2 font-semibold text-foreground">
                    <Microscope className="h-5 w-5 text-primary" />
                    Formül nasıl çalışır?
                  </h4>
                  <p className="pl-6 text-muted-foreground">
                    Vücut ağırlığınıza göre bir katsayı hesaplanır. Bu katsayı
                    toplam kaldırışınızla çarpılarak normalize edilmiş skorunuz
                    bulunur.
                  </p>
                </div>
              </div>

              <div className="group">
                <div className="rounded-lg border-border border-l-4 bg-card p-4">
                  <h4 className="mb-2 flex items-center gap-2 font-semibol text-foreground">
                    <Rocket className="h-5 w-5 text-primary" />
                    Skorumu nasıl artırabilirim?
                  </h4>
                  <p className="pl-6 text-muted-foreground">
                    En etkili yol kaldırışlarınızı artırmak. Ayrıca vücut
                    kompozisyonunuzu optimize ederek (kas artırıp yağ azaltarak)
                    katsayınızı iyileştirebilirsiniz.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Levels Visual */}
          <div className="rounded-2xl border border-border bg-card p-8">
            <h3 className="mb-8 text-center font-bold text-2xl text-foreground">
              <Award className="mr-2 inline h-6 w-6 text-primary" />
              Performans Seviyeleri
            </h3>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {[
                {
                  range: "700+",
                  level: "Dünya Klasmanı",
                },
                {
                  range: "600-700",
                  level: "Elit",
                  emoji: "",
                },
                {
                  range: "500-600",
                  level: "Çok İyi",
                },
                {
                  range: "400-500",
                  level: "İyi",
                },
                {
                  range: "300-400",
                  level: "Orta",
                },
                {
                  range: "0-300",
                  level: "Başlangıç",
                },
              ].map((item) => (
                <div className="group transition-transform duration-300 hover:scale-105">
                  <div
                    className={cn(
                      "rounded-xl border border-border bg-secondary p-4 text-center text-accent-foreground",
                    )}
                  >
                    <div className="font-bold text-sm">{item.level}</div>
                    <div className="text-xs opacity-90">{item.range}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 text-center">
              <p className="text-muted-foreground text-sm">
                * Erkekler için değerler. Kadınlar için yaklaşık 50-100 puan
                daha düşük değerler geçerlidir.
              </p>
            </div>
          </div>

          {/* Pro Tips */}
          <div className="rounded-2xl border border-border bg-card p-8">
            <h3 className="mb-6 flex items-center gap-2 font-bold text-2xl text-foreground">
              <Lightbulb className="h-6 w-6 text-primary" />
              Pro İpuçları
            </h3>

            <div className="grid gap-6 text-foreground md:grid-cols-2">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold">Yarışma Kurallarına Uyun</h4>
                    <p className="text-muted-foreground text-sm">
                      Powerlifting federasyonu kurallarına göre kaldırın
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold">Düzenli Test Edin</h4>
                    <p className="text-muted-foreground text-sm">
                      Aylık wilks skorunuzu takip edin
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold">
                      Zayıf Noktalarınızı Güçlendirin
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      En düşük kaldırışınıza odaklanın
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm">
                    4
                  </div>
                  <div>
                    <h4 className="font-semibold">
                      Diğer Sporcularla Karşılaştırın
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      Motivasyon için benzer yaştaki sporcuları takip edin
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
